import { clone, mergeDeepWithKey, omit } from "ramda";
import { ContainerNode, isContainerNode, LabelDocument, LabelItem, TemplateData, TemplateDocument, TemplateNode, TemplateNodeId, Variation } from "./LabelDocumentModel";

export function findNode(
    doc: TemplateNode[], 
    matcher: (node: TemplateNode)=>boolean
): TemplateNode|null {
    for (const node of doc) {
        if (matcher(node)) {
            return node;
        }
        if (isContainerNode(node)) {
            const found = findNode(node.children, matcher)
            if (found !== null) {
                return found;
            }
        }
    }
    return null;
}

export function findContainerNodeId(doc: TemplateNode[]): TemplateNodeId|null {
    return findNode(doc, node=>node.type==="container")?.id||null;
}

export function findNodeById(doc: TemplateNode[], nodeId: TemplateNodeId): TemplateNode|null {
    return findNode(doc, node=>node.id===nodeId);
}

export function applyVariation(
    templateData: TemplateData, 
    currentVariation: Variation,
    variation: Variation
): TemplateData {
    const newTemplateData = clone(templateData);
    
    for (const variatedNode of variation.nodes) {
        const nodeId = variatedNode.id;
        const variatedProps = omit(["id", "type"], variatedNode);
        const currentVariationNode = currentVariation.nodes.find(n=>n.id===nodeId);

        const templateNode = findNodeById(newTemplateData.document, nodeId);
        if (templateNode === null) continue;

        for (const vPropKeyNT in variatedProps) {
            const vPropKey = vPropKeyNT as keyof typeof variatedProps;
            const vPropValue = variatedProps[vPropKey];
            if (typeof vPropValue === "object") {
                templateNode[vPropKey] = mergeDeepWithKey(
                    (k, templateValue, variationValue) => {
                        const valueNotChanged = !currentVariationNode 
                            || !currentVariationNode[vPropKey]
                            || templateValue === (currentVariationNode[vPropKey] as any)[k];
                        return !valueNotChanged?templateValue:variationValue;
                    },
                    templateNode[vPropKey], vPropValue
                );
            } else {
                const valueNotChanged = !currentVariationNode
                    || typeof templateNode[vPropKey] === "undefined"
                    || templateNode[vPropKey] === currentVariationNode[vPropKey];
                if (valueNotChanged) {
                    // todo fix type
                    // @ts-ignore
                    templateNode[vPropKey] = vPropValue;
                }
            }
        }
    }
    return newTemplateData;
}

export function updateNode(node: TemplateNode, templateData: TemplateData): TemplateData {
    return {
        ...templateData,
        document: updateNodeInDocument(node, clone(templateData.document))
    };
}

export function updateNodeInDocument(node: TemplateNode, templateDocument: TemplateDocument): TemplateDocument {
    for (let nI = 0; nI < templateDocument.length; nI++) {
        const nV = templateDocument[nI];
        if (nV.id === node.id) {
            templateDocument.splice(nI, 1, node);
        } else if (isContainerNode(nV)) {
            nV.children = updateNodeInDocument(node, nV.children)
        }
    }
    return templateDocument;
}

export function resetNode(nodeId: TemplateNodeId, labelItem: LabelItem, labelDocument: LabelDocument): TemplateData {
    const templateData = labelItem.templateData;
    const node = findNodeById(templateData.document, nodeId);
    if (!node) return templateData;

    const originalTemplateData = resetTemplate(labelItem, labelDocument);
    const originalNode = findNodeById(originalTemplateData.document, nodeId);
    if (!originalNode) return templateData;

    return {
        ...templateData,
        document: updateNodeInDocument(originalNode, clone(templateData.document))
    }
}

export function resetTemplate(labelItem: LabelItem, labelDocument: LabelDocument): TemplateData {
    return applyVariation(
        labelDocument.template.templateData,
        labelItem.variation,
        labelItem.variation
    )
}

export function resetBackground(labelItem: LabelItem, labelDocument: LabelDocument): TemplateData {
    const templateData = labelItem.templateData;
    const doc = templateData.document;
    const conatinerNodeId = findContainerNodeId(doc);
    if (!conatinerNodeId) return labelItem.templateData;
    const containerNode = findNodeById(doc, conatinerNodeId) as ContainerNode;

    const originalContainerNode = findNodeById(
        resetTemplate(labelItem, labelDocument).document, 
        conatinerNodeId
    ) as ContainerNode;
    originalContainerNode.children = containerNode.children;
    return {
        ...templateData,
        document: updateNodeInDocument(originalContainerNode, clone(templateData.document))
    }
}
